/* eslint-disable react/prop-types */
"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { formatChatLocationDisplayAddress } from "../../utils/chatStructuredPayloads";
import AppModalShell, { AppModalIconButton } from "../common/AppModalShell";
import { getCurrentPosition, openExternalUrl } from "../../native/runtime";

const MAP_STYLE_URL =
  "https://api.maptiler.com/maps/0196259d-93a4-75bb-af54-6b003b7377ab/style.json?key=QMa6CZX7KdzCIRISMOjh";

function getMarkerDomId(marker) {
  return String(marker?.messageId ?? marker?.id ?? `${marker?.lat || ""}:${marker?.lng || ""}`);
}

function findMarkerByMessageId(markers, messageId) {
  if (messageId == null) return null;
  return markers.find((marker) => String(marker.messageId) === String(messageId)) || null;
}

function focusMapOnMarker(map, marker, { immediate = false, minZoom = 15 } = {}) {
  if (!map || !marker) return;

  const lng = Number(marker.lng);
  const lat = Number(marker.lat);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

  const currentZoom = Number(map.getZoom?.());
  const zoom = Math.max(Number.isFinite(currentZoom) ? currentZoom : minZoom, minZoom);
  const options = {
    center: [lng, lat],
    zoom,
    essential: true,
  };

  if (immediate) map.jumpTo(options);
  else map.flyTo(options);
}

function buildNavigationUrl(marker) {
  if (!marker) return;
  const lat = Number(marker.lat);
  const lng = Number(marker.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
}

function openExternalRoute(marker) {
  const url = buildNavigationUrl(marker);
  if (!url) return;
  void openExternalUrl(url);
}

function formatLocationAge(createdAt, fallbackTime = "") {
  const ts = new Date(createdAt || "").getTime();
  if (!Number.isFinite(ts)) return String(fallbackTime || "").trim();

  const diffMs = Date.now() - ts;
  if (!Number.isFinite(diffMs) || diffMs < 0) return String(fallbackTime || "").trim();

  const minutes = Math.max(0, Math.floor(diffMs / 60000));
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return String(fallbackTime || "").trim();
}

function dispatchLocationError(message) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("global-error", {
      detail: { message, status: 400 },
    })
  );
}

export default function ChatLocationMapModal({
  open,
  markers = [],
  initialMessageId = null,
  onClose,
  canDeleteMarker = null,
  onDeleteMarker = null,
}) {
  const { t } = useTranslation("menu");
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRefs = useRef([]);
  const userMarkerRef = useRef(null);
  const listItemRefs = useRef(new Map());
  const selectedIdRef = useRef(null);
  const [selectedId, setSelectedId] = useState(initialMessageId || markers?.[0]?.messageId || null);
  const [locating, setLocating] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState(null);

  const safeMarkers = useMemo(
    () =>
      (Array.isArray(markers) ? markers : [])
        .map((marker) => {
          const lat = Number(marker?.lat);
          const lng = Number(marker?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return {
            ...marker,
            lat,
            lng,
            address: formatChatLocationDisplayAddress(
              marker?.address,
              `${lat.toFixed(5)}, ${lng.toFixed(5)}`
            ),
          };
        })
        .filter(Boolean),
    [markers]
  );

  useEffect(() => {
    if (!open) return;
    const requestedMarker = findMarkerByMessageId(safeMarkers, initialMessageId);
    setSelectedId(requestedMarker?.messageId || safeMarkers[0]?.messageId || null);
    setDeletingMessageId(null);
  }, [initialMessageId, open, safeMarkers]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const selectedMarker = useMemo(() => {
    if (!safeMarkers.length) return null;
    return (
      findMarkerByMessageId(safeMarkers, selectedId) ||
      safeMarkers[0]
    );
  }, [safeMarkers, selectedId]);
  const selectedMarkerCanDelete = Boolean(
    selectedMarker &&
      typeof canDeleteMarker === "function" &&
      canDeleteMarker(selectedMarker)
  );

  const deleteSelectedMarker = async () => {
    if (
      !selectedMarkerCanDelete ||
      !selectedMarker ||
      deletingMessageId != null ||
      typeof onDeleteMarker !== "function"
    ) {
      return;
    }

    setDeletingMessageId(selectedMarker.messageId);
    try {
      const deleted = await onDeleteMarker(selectedMarker);
      if (!deleted) return;

      const remainingMarkers = safeMarkers.filter(
        (marker) => String(marker.messageId) !== String(selectedMarker.messageId)
      );
      if (!remainingMarkers.length) {
        onClose?.();
        return;
      }
      setSelectedId(remainingMarkers[0].messageId);
    } finally {
      setDeletingMessageId(null);
    }
  };

  useEffect(() => {
    if (!open || !safeMarkers.length) return undefined;

    let cancelled = false;
    let map = null;
    const init = () => {
      if (cancelled || !mapContainerRef.current) return;
      const initialMarker = findMarkerByMessageId(safeMarkers, initialMessageId);
      const first = initialMarker || safeMarkers[0];
      const hasExplicitInitialMarker = Boolean(initialMarker);
      const activeMarkerId = getMarkerDomId(first);
      try {
        map = new maplibregl.Map({
          container: mapContainerRef.current,
          style: MAP_STYLE_URL,
          center: [Number(first.lng), Number(first.lat)],
          zoom: hasExplicitInitialMarker || safeMarkers.length === 1 ? 15 : 12,
          attributionControl: false,
        });
        mapRef.current = map;

        markerRefs.current = safeMarkers.map((marker) => {
          const el = document.createElement("button");
          el.type = "button";
          el.className = "vg-chat-location-marker";
          if (getMarkerDomId(marker) === activeMarkerId) {
            el.classList.add("vg-chat-location-marker--active");
          }
          el.innerHTML = `<span>${String(marker.authorLabel || "User").slice(0, 18)}</span>`;
          el.addEventListener("click", () => {
            setSelectedId(marker.messageId);
            focusMapOnMarker(map, marker);
          });
          const markerInstance = new maplibregl.Marker({ element: el, anchor: "bottom" })
            .setLngLat([Number(marker.lng), Number(marker.lat)])
            .addTo(map);
          return {
            id: getMarkerDomId(marker),
            element: el,
            marker: markerInstance,
          };
        });

        map.on("load", () => {
          if (cancelled) return;
          map.resize();
          if (hasExplicitInitialMarker) {
            focusMapOnMarker(
              map,
              findMarkerByMessageId(safeMarkers, selectedIdRef.current) || first,
              { immediate: true }
            );
          } else if (safeMarkers.length > 1) {
            const bounds = new maplibregl.LngLatBounds();
            safeMarkers.forEach((marker) => bounds.extend([Number(marker.lng), Number(marker.lat)]));
            map.fitBounds(bounds, { padding: 70, maxZoom: 15, duration: 0 });
          }
        });
      } catch {
        // Keep the shell visible even if the map provider fails.
      }
    };

    const frame = window.requestAnimationFrame(init);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      markerRefs.current.forEach((marker) => {
        try {
          marker.marker?.remove?.();
        } catch {
          // ignore
        }
      });
      markerRefs.current = [];
      try {
        userMarkerRef.current?.remove?.();
      } catch {
        // ignore
      }
      userMarkerRef.current = null;
      try {
        mapRef.current?.remove?.();
      } catch {
        // ignore
      }
      mapRef.current = null;
      setLocating(false);
    };
  }, [initialMessageId, open, safeMarkers]);

  useEffect(() => {
    if (!open || !selectedMarker || !mapRef.current) return;
    focusMapOnMarker(mapRef.current, selectedMarker);

    const activeId = getMarkerDomId(selectedMarker);
    markerRefs.current.forEach((entry) => {
      entry.element?.classList.toggle("vg-chat-location-marker--active", entry.id === activeId);
    });

    const activeListItem = listItemRefs.current.get(activeId);
    try {
      activeListItem?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    } catch {
      // ignore
    }
  }, [open, selectedMarker]);

  const focusViewerLocation = async () => {
    const map = mapRef.current;
    if (!map) return;

    setLocating(true);
    try {
      const position = await getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 9000,
        maximumAge: 30000,
      });
        const lat = Number(position.coords.latitude);
        const lng = Number(position.coords.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          setLocating(false);
          return;
        }

        if (!userMarkerRef.current) {
          const el = document.createElement("div");
          el.className = "vg-chat-user-location-marker";
          el.innerHTML = "<span />";
          userMarkerRef.current = new maplibregl.Marker({ element: el, anchor: "center" })
            .setLngLat([lng, lat])
            .addTo(map);
        } else {
          userMarkerRef.current.setLngLat([lng, lat]);
        }

        map.flyTo({
          center: [lng, lat],
          zoom: Math.max(map.getZoom?.() || 14, 15),
          essential: true,
        });
        setLocating(false);
    } catch {
      setLocating(false);
      dispatchLocationError("Could not detect your current location.");
    }
  };

  return (
    <AppModalShell
      open={open}
      title="Locations"
      onClose={onClose}
      closeAriaLabel="Close locations"
      leftAction={
        selectedMarkerCanDelete ? (
          <AppModalIconButton
            onClick={deleteSelectedMarker}
            disabled={deletingMessageId != null}
            aria-busy={deletingMessageId != null}
            aria-label={t("chats.event_chat.location_viewer.delete", {
              defaultValue: "Delete location",
            })}
            className="bg-red-500/75 text-white hover:bg-red-500/85"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" className={`h-5 w-5 ${deletingMessageId != null ? "opacity-70" : ""}`} fill="none">
              <path d="M4 7H20M9 7V4H15V7M7 7L8 20H16L17 7M10 11V16M14 11V16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </AppModalIconButton>
        ) : null
      }
      maxWidthClassName="max-w-[720px]"
      desktopMaxHeight="min(86vh, 820px)"
      zIndexClassName="z-[95]"
      bodyClassName="flex min-h-0 flex-col gap-4 pb-5 pt-1"
      contentClassName="min-h-0"
    >
      <div className="relative h-[min(46dvh,420px)] min-h-[280px] shrink-0 overflow-hidden rounded-[24px] bg-[#D9D9D9]">
        <div ref={mapContainerRef} className="h-full w-full bg-[#D9D9D9]" />
        {!safeMarkers.length ? (
          <div className="absolute inset-0 grid place-items-center bg-[#0D1428] px-8 text-center text-[16px] font-semibold text-white/70">
            No locations in this chat yet.
          </div>
        ) : null}
        {safeMarkers.length ? (
          <>
            <button
              type="button"
              onClick={focusViewerLocation}
              disabled={locating}
              aria-busy={locating}
              className="absolute bottom-[76px] right-6 flex h-14 w-14 items-center justify-center rounded-full border border-white/35 bg-[#3E6FFB] text-white shadow-[0_14px_28px_rgba(20,46,120,0.35)] transition active:scale-95 disabled:opacity-65"
              aria-label="Use current location"
            >
              <img
                src="/assets/menu/location_icon.svg"
                alt=""
                className={`h-7 w-7 brightness-0 invert ${locating ? "opacity-70" : ""}`}
                aria-hidden="true"
              />
            </button>
            {selectedMarker ? (
              <button
                type="button"
                onClick={() => openExternalRoute(selectedMarker)}
                className="absolute bottom-4 left-4 right-4 h-12 rounded-full bg-white px-5 text-[16px] font-bold text-[#19233A] shadow-[0_14px_30px_rgba(0,0,0,0.22)] transition active:scale-[0.985]"
              >
                Navigate
              </button>
            ) : null}
          </>
        ) : null}
      </div>

      {safeMarkers.length ? (
        <div className="relative z-20 min-h-0 shrink-0">
          <div className="max-h-[min(30dvh,260px)] space-y-2 overflow-y-auto pr-1">
            {safeMarkers.map((marker) => {
              const active = String(marker.messageId) === String(selectedMarker?.messageId);
              const markerId = getMarkerDomId(marker);
              return (
                <button
                  key={markerId}
                  ref={(node) => {
                    if (node) listItemRefs.current.set(markerId, node);
                    else listItemRefs.current.delete(markerId);
                  }}
                  type="button"
                  onClick={() => setSelectedId(marker.messageId)}
                  className={`w-full rounded-[24px] border px-4 py-3 text-left transition active:scale-[0.99] ${
                    active
                      ? "border-[#6F93FF] bg-[#3E6FFB]/24 shadow-[0_12px_24px_rgba(62,111,251,0.16)]"
                      : "border-white/14 bg-white/10 hover:bg-white/14"
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="max-w-[38%] shrink-0 truncate text-[15px] font-bold leading-[20px] text-white">
                      {marker.authorLabel || "User"}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-semibold leading-[18px] text-white/72">
                      {marker.address}
                    </span>
                    <span className="shrink-0 text-[12px] font-bold leading-[16px] text-white/50">
                      {formatLocationAge(marker.createdAt, marker.time)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </AppModalShell>
  );
}
